import { useMemo, useState } from 'react';
import { ArrowRightLeft, Castle, CircleCheck, ListPlus, Plus, SearchX, X } from 'lucide-react';
import * as api from '../worlds.api';
import { toast, useWorldsStore } from '../worlds.store';
import { fmtInt } from '../constants';
import type { Country, GeoRegion } from '../worlds.types';
import { confirmAction } from './ConfirmDialog';
import { FlagThumb } from './FlagThumb';
import { Modal } from './Modal';
import { ColorChip, EmptyRow, SearchField, SoftButton, StatusBadge, TintBadge } from './ui';
import { fold, matchesSearch } from './geoRegions.utils';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import rs from '../tabs/GeoRegionsTab.module.scss';

/** Pays cités dans la confirmation d'un ajout en masse (au-delà : « et N autres »). */
const LIST_MAX = 8;

/**
 * « Pays de la région X » : ajouter, retirer ou réaffecter des pays. Un pays appartient à une
 * région au plus, donc ajouter un pays d'une autre région le réaffecte.
 *
 * Garde de concurrence : Retirer et Réaffecter envoient la région vue par l'interface
 * (fromGeoRegionId). Si le pays a changé de région entre-temps (autre onglet, autre admin, vue
 * vieille de 10 s au plus), le satellite ne touche à rien et le compte dans `skipped` : on le dit
 * et on recharge la liste. Ajouter (pays sans région) n'a pas de garde : « sans région » ne se
 * désigne pas côté satellite.
 */
export function RegionCountriesModal({ region, readOnly, onClose }: {
  region: GeoRegion;
  readOnly: boolean;
  onClose: () => void;
}) {
  const countries = useWorldsStore((s) => s.countries);
  const regions = useWorldsStore((s) => s.geoRegions);
  const putCountry = useWorldsStore((s) => s.putCountry);
  const reloadCountries = useWorldsStore((s) => s.reloadCountries);

  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<ReadonlySet<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const regionById = useMemo(() => new Map(regions.map((r) => [r.id, r])), [regions]);
  const insideCount = useMemo(() => countries.filter((c) => c.geoRegionId === region.id).length, [countries, region.id]);

  const search = fold(q.trim());
  const list = useMemo(() => {
    // Les pays de la région d'abord, puis ceux sans région (candidats naturels), puis les autres.
    const rank = (c: Country) => (c.geoRegionId === region.id ? 0 : !c.geoRegionId ? 1 : 2);
    return countries
      .filter((c) => matchesSearch(search, c.slug, c.slugEn, c.empireNameFr, c.empireNameEn, c.isoCode))
      .sort((a, b) => rank(a) - rank(b) || (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0));
  }, [countries, search, region.id]);
  const candidates = useMemo(() => list.filter((c) => c.geoRegionId !== region.id), [list, region.id]);

  const markBusy = (id: string, on: boolean) => setBusy((prev) => {
    const next = new Set(prev);
    if (on) next.add(id); else next.delete(id);
    return next;
  });

  /** Écriture locale sans attendre le poll (champs calculés du niveau courant conservés par le store). */
  const applyLocal = (ids: Set<string>, geoRegionId: string | null) => {
    useWorldsStore.getState().countries.forEach((c) => {
      if (ids.has(c.id) && c.geoRegionId !== geoRegionId) putCountry({ ...c, geoRegionId });
    });
  };

  const assignOne = async (c: Country, target: string | null) => {
    const from = c.geoRegionId;
    markBusy(c.id, true);
    const res = await api.assignGeoRegion([c.id], target, from);
    markBusy(c.id, false);
    if (!res.data) { toast.error(res.error ?? 'Affectation en échec'); return; }
    if (res.data.updated === 0 && (res.data.skipped ?? 0) > 0) {
      toast.warning(`${c.slug} a changé de région entre-temps : rien n'a été modifié`);
      void reloadCountries();
      return;
    }
    applyLocal(new Set([c.id]), target);
  };

  const addAll = async () => {
    const targets = candidates;
    if (targets.length === 0) return;
    const moving = targets.filter((c) => c.geoRegionId);
    if (moving.length > 0) {
      const ok = await confirmAction({
        title: 'Ajouter les résultats',
        tone: 'primary',
        confirmLabel: `Ajouter ${fmtInt(targets.length)} pays`,
        message: (
          <>Ajouter les <strong>{fmtInt(targets.length)}</strong> pays affichés à la région <strong>{region.name}</strong> ?</>
        ),
        details: (
          <>
            <div>
              {moving.length === 1 ? '1 pays quittera sa région actuelle :' : `${fmtInt(moving.length)} pays quitteront leur région actuelle :`}
            </div>
            <ul>
              {moving.slice(0, LIST_MAX).map((c) => (
                <li key={c.id}><strong>{c.slug}</strong> ({regionById.get(c.geoRegionId ?? '')?.name ?? 'région inconnue'})</li>
              ))}
              {moving.length > LIST_MAX && <li>et {fmtInt(moving.length - LIST_MAX)} autres</li>}
            </ul>
          </>
        ),
      });
      if (!ok) return;
    }
    // Une seule source commune (non nulle) : on peut poser la garde ; sources mêlées : sans garde.
    const sources = new Set(targets.map((c) => c.geoRegionId));
    const from = sources.size === 1 ? [...sources][0] : null;
    setBulkBusy(true);
    const res = await api.assignGeoRegion(targets.map((c) => c.id), region.id, from);
    setBulkBusy(false);
    if (!res.data) { toast.error(res.error ?? 'Affectation en échec'); return; }
    const { updated } = res.data;
    const skipped = res.data.skipped ?? 0;
    if (skipped > 0) {
      const moved = skipped === 1 ? '1 pays avait changé de région entre-temps' : `${fmtInt(skipped)} pays avaient changé de région entre-temps`;
      toast.warning(updated === 0
        ? `Rien n'a été modifié : ${moved}`
        : `${fmtInt(updated)} pays ajoutés à ${region.name} ; ${moved} et n'${skipped === 1 ? 'a' : 'ont'} pas bougé`);
      void reloadCountries();
      return;
    }
    applyLocal(new Set(targets.map((c) => c.id)), region.id);
    toast.success(updated === 1 ? `1 pays ajouté à ${region.name}` : `${fmtInt(updated)} pays ajoutés à ${region.name}`);
  };

  const rowAction = (c: Country, other: GeoRegion | undefined) => {
    const inside = c.geoRegionId === region.id;
    const common = { busy: busy.has(c.id), disabled: bulkBusy } as const;
    if (inside) {
      return (
        <SoftButton tone="danger" icon={X} {...common} title={`Retirer ${c.slug} de ${region.name}`}
          onClick={() => void assignOne(c, null)}>
          Retirer
        </SoftButton>
      );
    }
    if (c.geoRegionId) {
      return (
        <SoftButton tone="success" icon={ArrowRightLeft} {...common}
          title={`Retirer ${c.slug} de ${other?.name ?? 'sa région'} et l'ajouter à ${region.name}`}
          onClick={() => void assignOne(c, region.id)}>
          Réaffecter
        </SoftButton>
      );
    }
    return (
      <SoftButton tone="success" icon={Plus} {...common} title={`Ajouter ${c.slug} à ${region.name}`}
        onClick={() => void assignOne(c, region.id)}>
        Ajouter
      </SoftButton>
    );
  };

  const where = (c: Country, other: GeoRegion | undefined) => {
    if (c.geoRegionId === region.id) return <StatusBadge tone="ok" icon={CircleCheck}>dans la région</StatusBadge>;
    if (!c.geoRegionId) return <StatusBadge tone="off">sans région</StatusBadge>;
    if (!other) return <StatusBadge tone="off" title="Région absente de la liste chargée">région inconnue</StatusBadge>;
    return (
      <TintBadge color={other.color} title={readOnly ? `Dans la région ${other.name}` : `L'ajouter ici la retirera de ${other.name}`}>
        autre région : {other.name}
      </TintBadge>
    );
  };

  const colSpan = readOnly ? 1 : 2;

  return (
    <Modal size="wide" title={`Pays de la région ${region.name}`} onClose={onClose} busy={bulkBusy}
      icon={<ColorChip color={region.color} size={24} title={region.color} />}
      subtitle={insideCount === 1 ? '1 pays dans la région' : `${fmtInt(insideCount)} pays dans la région`}
      footer={<button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={bulkBusy}>Fermer</button>}>
      <SearchField value={q} onChange={setQ} autoFocus placeholder="Rechercher un pays (nom, EN, empire, ISO)" />
      <div className={rs.bulkBar}>
        <span className={ws.counter}>
          {search ? (
            <><strong>{fmtInt(list.length)}</strong> résultat{list.length > 1 ? 's' : ''} sur {fmtInt(countries.length)}</>
          ) : (
            <><strong>{fmtInt(countries.length)}</strong> pays</>
          )}
          {' · '}<strong>{fmtInt(insideCount)}</strong> dans la région
        </span>
        {!readOnly && search && candidates.length > 0 && (
          <SoftButton tone="success" icon={ListPlus} busy={bulkBusy} onClick={() => void addAll()}
            title="Ajoute à la région, en une fois, les pays affichés qui n'y sont pas encore">
            {candidates.length === 1 ? 'Ajouter le résultat' : `Ajouter les ${fmtInt(candidates.length)} résultats`}
          </SoftButton>
        )}
      </div>
      <div className={`${ws.tableWrap} ${rs.listWrap}`}>
        <table className={ws.table}>
          <tbody>
            {list.map((c) => {
              const other = c.geoRegionId && c.geoRegionId !== region.id ? regionById.get(c.geoRegionId) : undefined;
              const sub = [
                c.slugEn && c.slugEn !== c.slug ? `EN : ${c.slugEn}` : null,
                c.empireNameFr ? `Empire : ${c.empireNameFr}` : null,
                c.enabled ? null : 'inactif',
              ].filter(Boolean).join(' · ');
              return (
                <tr key={c.id}>
                  <td className={rs.countryTd}>
                    <span className={rs.countryCell}>
                      <FlagThumb flag={c.flag} width={20} />
                      <ColorChip color={c.color} size={16} title={c.color} />
                      <span className={rs.countryText}>
                        <span className={rs.countryLine}>
                          <span className={rs.countrySlug}>{c.slug}</span>
                          {where(c, other)}
                        </span>
                        {sub && <span className={rs.countrySub}>{sub}</span>}
                      </span>
                    </span>
                  </td>
                  {!readOnly && <td className={rs.rowAction}>{rowAction(c, other)}</td>}
                </tr>
              );
            })}
            {list.length === 0 && (countries.length === 0 ? (
              <EmptyRow colSpan={colSpan} icon={Castle} title="Aucun pays"
                text="Les pays se créent dans l'onglet Pays, ou par l'import NG Java." />
            ) : (
              <EmptyRow colSpan={colSpan} icon={SearchX} title="Aucun pays ne correspond"
                text={`Aucun nom, nom EN, nom d'empire ni code ISO ne contient « ${q.trim()} ».`}
                actions={(
                  <button type="button" className={styles.secondaryBtn} onClick={() => setQ('')}>
                    <X size={14} /> Effacer la recherche
                  </button>
                )} />
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
